import { useEffect, useMemo, useRef, useState } from 'react'
import {
  PROJECT_BILLING_SCHEMES,
  PROJECT_POSITION_STATUSES_READONLY,
  billingIntervalAllowed,
  billingIntervalsFor,
} from '@symphenia/shared-types'
import { api } from '@/lib/axios'
import { formCard } from '@/lib/form-styles'
import {
  Abschnitt,
  FeldStatus,
  FeldZeile,
  Feldgruppe,
  feldKlasse,
  leseZahl,
  mitStand,
  useEntwurf,
  useFeld,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern'
import { useTerminology } from '@/lib/useTerminology'
import { fetchUnits } from '../api/project-bom.api'
import { searchArticles } from '../api/project-sales.api'
import { fetchPartner, type PartnerAuswahl } from '../api/projects-head.api'
import { fetchPositions } from '../api/projects-phase-d.api'
import { BILLING_INTERVAL_LABELS, BILLING_SCHEME_LABELS, PROGRESS_SOURCE_LABELS, alsTag } from '../project-labels'
import type { PositionDetail, PositionGroup } from '../types/phase-d.types'
import type { ProjectDetail } from '../types/project.types'
import { Vorschlaege, useSuche } from './zeilenblock'

/**
 * AP-35 Detailblatt 2 8.3 — **Reiter Details der Position.**
 *
 * **Seit AP-36 Phase F ohne Stift, feldweise:** Die Felder stehen immer da und
 * speichern beim Verlassen, Auswahl sofort — über die eine Speicherstelle der
 * Position (`.claude/rules/speichern.md`).
 *
 * Vererbbare Felder (Blatt 2 5.3) tragen ihre Herkunft: leer gelassen heißt
 * „vom Projekt", und ein geleertes Feld schickt `null` — das ist „zurück auf
 * Projekt".
 *
 * Abgerechnet und storniert sind gesperrt (`PROJECT_POSITION_STATUSES_READONLY`)
 * — die Felder stehen dann gesperrt da und sehen aus wie Text.
 */

type Einheit = { id: string; code: string }
type Artikel = { id: string; name: string; articleNumber: string; unit: { id: string; code: string } | null }

/**
 * Der Datensatz, wie ihn die Speicherstelle braucht: Die vererbbaren Felder
 * liefert der Server unter `inherited.<feld>`, bearbeitet wird der **eigene**
 * Wert — und genau der geht als `<feld>` wieder hinaus.
 */
function alsDaten(position: PositionDetail): Datensatz {
  const flach: Datensatz = { ...(position as unknown as Datensatz) }
  for (const [k, v] of Object.entries(position.inherited ?? {})) flach[k] = (v as { own?: unknown } | null)?.own ?? null
  return flach
}

/**
 * **Die eine Speicherstelle der Position** (`position:<id>`). Der Reiter Details und der Kopf der Position
 * (Bezeichnung an der Überschrift, Auftraggeber 13.10.2026) schreiben dieselbe Position — sie holen die Stelle
 * hier, damit beide dieselben Daten, denselben Schreibweg und dieselbe Warteschlange haben (`speichern.md`).
 */
export function usePositionStelle(projekt: ProjectDetail, position: PositionDetail, onChanged: () => Promise<void> | void): Speicherstelle<unknown> {
  const terms = useTerminology()
  const daten = useMemo(() => alsDaten(position), [position])
  return useSpeicherstelle<Datensatz>({
    schluessel: `position:${position.id}`,
    bezeichnung: `${terms.position} ${position.number}`,
    daten,
    schreibe: async (patch, stand) => {
      const res = await api.patch<{ data: PositionDetail }>(
        `/projects/${projekt.id}/positions/${position.id}`,
        patch,
        mitStand(stand),
      )
      void onChanged()
      return alsDaten(res.data.data)
    },
    keepalive: { url: `/projects/${projekt.id}/positions/${position.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>
}

export function PositionDetailsTab({
  projekt,
  position,
  darfPflegen,
  onChanged,
}: {
  projekt: ProjectDetail
  position: PositionDetail
  darfPflegen: boolean
  onChanged: () => Promise<void> | void
}) {
  const terms = useTerminology()
  const offen = darfPflegen && !PROJECT_POSITION_STATUSES_READONLY.includes(position.status)
  const [einheiten, setEinheiten] = useState<Einheit[]>([])
  const [gruppen, setGruppen] = useState<PositionGroup[]>([])
  const [partner, setPartner] = useState<PartnerAuswahl | null>(null)

  // Die Auswahllisten stehen immer — ein gesperrtes Feld zeigt seinen Wert, kein leeres Dropdown.
  useEffect(() => {
    fetchUnits().then(setEinheiten).catch(() => undefined)
    fetchPositions(projekt.id)
      .then((r) => setGruppen(r.groups))
      .catch(() => undefined)
  }, [projekt.id])

  useEffect(() => {
    if (!projekt.clientId) {
      setPartner(null)
      return
    }
    void fetchPartner(projekt.clientId)
      .then(setPartner)
      .catch(() => setPartner(null))
  }, [projekt.clientId])

  const stelle = usePositionStelle(projekt, position, onChanged)

  return (
    <div className={`${formCard} grid gap-4 lg:grid-cols-2`}>
      <PositionKarte stelle={stelle} gesperrt={!offen} einheiten={einheiten} gruppen={gruppen} titel={terms.position} />
      {/* Termine und Fortschritt stehen seit Nachtrag 3 im Reiter „Phasen & Zuständige", die Montageanweisung bei den Arbeitsschritten. */}
      <KundenauftragKarte stelle={stelle} position={position} gesperrt={!offen} partner={partner} titelProjekt={terms.project} />
    </div>
  )
}

// ── Position ───────────────────────────────────────────────────────────────

function PositionKarte({
  stelle,
  gesperrt,
  einheiten,
  gruppen,
  titel,
}: {
  stelle: Speicherstelle<unknown>
  gesperrt: boolean
  einheiten: Einheit[]
  gruppen: PositionGroup[]
  titel: string
}) {
  const daten = useStellenDaten(stelle)
  const name = useFeld(stelle, 'name', { label: 'Bezeichnung', pflicht: true, gesperrt })
  const art = useFeld(stelle, 'type', { art: 'auswahl', label: 'Art', gesperrt })
  const gruppe = useFeld<string | null>(stelle, 'groupId', { art: 'auswahl', label: 'Gruppe', gesperrt })
  const nachtrag = useFeld(stelle, 'supplementNote', { label: 'Anlass des Nachtrags', gesperrt })
  const kundenNummer = useFeld(stelle, 'customerArticleNumber', { label: 'Kunden-Artikelnr.', gesperrt })
  const langtext = useFeld(stelle, 'longText', { label: 'Langtext', gesperrt })

  return (
    <Abschnitt titel={titel}>
      <FeldZeile label="Bezeichnung" status={name}>
        <input className={`${feldKlasse} font-semibold`} {...name.props} />
      </FeldZeile>

      {/* Menge und Einheit gehören zusammen — sie gehen beim Verlassen der Gruppe gemeinsam hinaus. */}
      <Feldgruppe stelle={stelle} label="Menge">
        <MengenFelder stelle={stelle} gesperrt={gesperrt} einheiten={einheiten} />
      </Feldgruppe>

      <FeldZeile label="Art" status={art}>
        <select className={feldKlasse} {...art.props}>
          <option value="SERVICE">Leistung</option>
          <option value="SUPPLEMENT">Nachtrag</option>
        </select>
      </FeldZeile>
      {art.wert === 'SUPPLEMENT' && (
        <FeldZeile label="Anlass des Nachtrags" status={nachtrag}>
          <input className={feldKlasse} {...nachtrag.props} />
        </FeldZeile>
      )}
      <FeldZeile label="Gruppe" status={gruppe}>
        <select className={feldKlasse} {...gruppe.props}>
          <option value="">— ohne Gruppe —</option>
          {gruppen.map((g) => (
            <option key={g.id} value={g.id}>
              {g.number}. {g.name}
            </option>
          ))}
        </select>
      </FeldZeile>

      <ArtikelFeld stelle={stelle} gesperrt={gesperrt} />

      <FeldZeile label="Kunden-Artikelnr." status={kundenNummer}>
        <input className={feldKlasse} {...kundenNummer.props} />
      </FeldZeile>
      <FeldZeile label="Langtext" status={langtext}>
        <textarea className={feldKlasse} rows={4} {...langtext.props} />
      </FeldZeile>
      {daten.supplementNo != null && art.wert === 'SUPPLEMENT' && (
        <FeldZeile label="Nachtragsnummer">N{String(daten.supplementNo)}</FeldZeile>
      )}
    </Abschnitt>
  )
}

/** Menge und Einheit — der Hook läuft im Kontext der `Feldgruppe`. */
function MengenFelder({ stelle, gesperrt, einheiten }: { stelle: Speicherstelle<unknown>; gesperrt: boolean; einheiten: Einheit[] }) {
  const menge = useFeld<number | null>(stelle, 'quantity', { art: 'zahl', label: 'Menge', pflicht: true, gesperrt })
  const einheit = useFeld<string | null>(stelle, 'unitId', { art: 'auswahl', label: 'Einheit', gesperrt })

  return (
    <FeldZeile label="Menge" status={menge}>
      <span className="flex gap-2">
        <input className={`${feldKlasse} w-28 text-right`} inputMode="decimal" {...menge.props} />
        <select className={`${feldKlasse} w-28`} {...einheit.props}>
          <option value="">—</option>
          {einheiten.map((u) => (
            <option key={u.id} value={u.id}>
              {u.code}
            </option>
          ))}
        </select>
      </span>
    </FeldZeile>
  )
}

/**
 * Der Artikel: gesucht wird im Feld, gespeichert erst die **Wahl**. Ein
 * geleertes Feld löst den Artikel; ein getippter, nicht gewählter Text ändert
 * nichts. Die Einheit des Artikels geht als Folgefeld im selben PATCH mit.
 */
function ArtikelFeld({ stelle, gesperrt }: { stelle: Speicherstelle<unknown>; gesperrt: boolean }) {
  const daten = useStellenDaten(stelle)
  const gewaehlt = useRef<Artikel | null>(null)
  const artikel = useFeld<string | null>(stelle, 'articleId', {
    art: 'auswahl',
    label: 'Artikel',
    gesperrt,
    folge: (w) => (w && gewaehlt.current?.id === w && gewaehlt.current.unit ? { unitId: gewaehlt.current.unit.id } : {}),
  })
  const gespeichert = daten.article as { id: string; name: string; articleNumber: string } | null | undefined
  const gespeicherterText = gespeichert ? `${gespeichert.name} (${gespeichert.articleNumber})` : ''
  const [eingabe, setEingabe] = useState<string | null>(null)
  const [suchen, setSuchen] = useState(false)
  const sichtbar = eingabe ?? gespeicherterText
  const [treffer, setTreffer] = useSuche<Artikel>(sichtbar, suchen, searchArticles)

  return (
    <FeldZeile label="Artikel" status={artikel}>
      <span className="relative block">
        <input
          className={feldKlasse}
          placeholder="Artikel suchen — leer löst den Artikel"
          disabled={gesperrt || undefined}
          value={sichtbar}
          onChange={(e) => {
            setEingabe(e.target.value)
            setSuchen(true)
          }}
          onBlur={() => {
            setSuchen(false)
            if (eingabe === null) return
            if (eingabe.trim() === '' && artikel.wert) artikel.setze(null)
            setEingabe(null)
          }}
        />
        <Vorschlaege
          allein
          eintraege={treffer}
          schluessel={(a) => a.id}
          onWahl={(a) => {
            gewaehlt.current = a
            setEingabe(null)
            setSuchen(false)
            setTreffer([])
            artikel.setze(a.id)
          }}
          darstellung={(a) => (
            <>
              {a.name} <span className="font-mono text-[11px] text-muted-foreground">{a.articleNumber}</span>
            </>
          )}
        />
      </span>
    </FeldZeile>
  )
}

// ── Termine und Fortschritt ────────────────────────────────────────────────

/**
 * Die Karte steht seit Nachtrag 3 im Reiter „Phasen & Zuständige". Geschrieben
 * wird über den Weg, den der Reiter mitgibt (`speichere`) — er lädt die
 * Position danach neu; die Speicherstelle übernimmt den gesendeten Stand
 * solange selbst.
 */
export function TermineKarte({
  position,
  offen,
  speichere,
  titel = 'Termine und Fortschritt',
}: {
  position: PositionDetail
  offen: boolean
  speichere: (d: Record<string, unknown>) => Promise<boolean>
  titel?: string
}) {
  const daten = useMemo(() => alsDaten(position), [position])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `position:${position.id}`,
    bezeichnung: `${position.number} ${position.name}`,
    daten,
    schreibe: async (patch) => {
      await speichere(patch)
      return patch
    },
  }) as Speicherstelle<unknown>

  const gesperrt = !offen
  const termin = useFeld(stelle, 'deliveryDueAt', { art: 'datum', label: 'Liefertermin', gesperrt })
  const zusatz = useFeld(stelle, 'deliveryDueNote', { label: 'Zusatz zum Termin', gesperrt })
  const anmerkung = useFeld(stelle, 'statusNote', { label: 'Anmerkung', gesperrt })
  const stand = useFeld<number | null>(stelle, 'progressPercent', {
    art: 'zahl',
    label: 'Leistungsstand',
    gesperrt,
    // Ganze Prozent — der Server führt den Handwert ohne Nachkommastellen.
    lies: (t) => {
      const n = leseZahl(t)
      return n === null ? null : Math.round(n)
    },
    pruefe: (w) => (w !== null && (w < 0 || w > 100) ? 'Zwischen 0 und 100' : null),
  })

  return (
    <Abschnitt titel={titel}>
      <FeldZeile label="Liefertermin" status={termin}>
        <input type="date" className={feldKlasse} {...termin.props} />
      </FeldZeile>
      <FeldZeile label="Zusatz zum Termin" status={zusatz}>
        <input className={feldKlasse} placeholder="KW 42, nach Freigabe …" {...zusatz.props} />
      </FeldZeile>
      <FeldZeile label="fertiggestellt am" hilfe="Setzt der Statuswechsel auf „fertig“.">
        {alsTag(position.completedAt)}
      </FeldZeile>
      <FeldZeile label="Leistungsstand (Handwert)" status={stand} hilfe="leer = aus Arbeitsschritten bzw. Status">
        <span className="flex items-center gap-2">
          <input className={`${feldKlasse} w-20 text-right`} inputMode="numeric" {...stand.props} />
          <span className="text-muted-foreground">%</span>
          <span className="text-xs text-muted-foreground">
            wirksam {position.progress.percent} % · {PROGRESS_SOURCE_LABELS[position.progress.source]}
          </span>
        </span>
      </FeldZeile>
      <FeldZeile label="Soll-Stunden">{position.progress.plannedHours || '—'}</FeldZeile>
      <FeldZeile label="Anmerkung" status={anmerkung}>
        <input className={feldKlasse} {...anmerkung.props} />
      </FeldZeile>
    </Abschnitt>
  )
}

// ── Kundenauftrag, Baustelle, Abrechnung — vererbbar (Blatt 2 5.3) ─────────

type Geerbt = { own: unknown; effective: unknown; inherited: boolean } | undefined

function text(wert: unknown): string {
  if (wert == null) return '—'
  if (typeof wert === 'object') {
    const a = wert as Record<string, string>
    return [a.recipient, a.street, [a.zip, a.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '—'
  }
  return String(wert)
}

/** Woher der wirksame Wert kommt — als Hilfstext unter dem Feld (4.1). */
function herkunft(wert: Geerbt, titelProjekt: string): string | undefined {
  if (!wert) return undefined
  return wert.inherited ? `vom ${titelProjekt}: ${text(wert.effective)}` : 'eigener Wert'
}

const TEXTFELDER = [
  ['customerOrderRef', 'Referenz'],
  ['customerOrderNumber', 'Kunden-Auftragsnummer'],
  ['customerOrderPosition', 'Kunden-Auftragsposition'],
] as const

function KundenauftragKarte({
  stelle,
  position,
  gesperrt,
  partner,
  titelProjekt,
}: {
  stelle: Speicherstelle<unknown>
  position: PositionDetail
  gesperrt: boolean
  partner: PartnerAuswahl | null
  titelProjekt: string
}) {
  const g = (k: string) => position.inherited?.[k] as Geerbt

  return (
    <Abschnitt titel={`Kundenauftrag, Baustelle, Abrechnung · vom ${titelProjekt} geerbt, wo leer`}>
      {TEXTFELDER.map(([k, label]) => (
        <GeerbtesTextfeld key={k} stelle={stelle} feld={k} label={label} gesperrt={gesperrt} geerbt={g(k)} titelProjekt={titelProjekt} />
      ))}

      <BaustelleFeld stelle={stelle} geerbt={g('siteAddressId')} freiGeerbt={g('siteAddressJson')} gesperrt={gesperrt} partner={partner} titelProjekt={titelProjekt} />

      {/* Schema und Intervall prüft der Server zusammen (Nachtrag 2) — also eine Gruppe. */}
      <Feldgruppe
        stelle={stelle}
        label="Abrechnung"
        pruefe={(werte) => {
          const schema = werte.billingScheme ? String(werte.billingScheme) : String(g('billingScheme')?.effective ?? '')
          const intervall = werte.billingInterval ? String(werte.billingInterval) : ''
          if (!schema || !intervall) return null
          return billingIntervalAllowed(schema, intervall) ? null : 'Das Intervall passt nicht zum Abrechnungsschema.'
        }}
      >
        <AbrechnungsFelder stelle={stelle} gesperrt={gesperrt} geerbtesSchema={String(g('billingScheme')?.effective ?? '')} titelProjekt={titelProjekt} />
      </Feldgruppe>
    </Abschnitt>
  )
}

function GeerbtesTextfeld({
  stelle,
  feld,
  label,
  gesperrt,
  geerbt,
  titelProjekt,
}: {
  stelle: Speicherstelle<unknown>
  feld: string
  label: string
  gesperrt: boolean
  geerbt: Geerbt
  titelProjekt: string
}) {
  const f = useFeld(stelle, feld, { label, gesperrt })
  return (
    <FeldZeile label={label} status={f} hilfe={herkunft(geerbt, titelProjekt)}>
      {/* Leer heißt „zurück auf das Projekt" — genau das bedeutet `null` am Server. */}
      <input className={feldKlasse} placeholder={geerbt?.inherited ? text(geerbt.effective) : ''} {...f.props} />
    </FeldZeile>
  )
}

type BaustelleEntwurf = { wahl: string; frei: Record<string, string> }

/**
 * Die Baustelle ist ein Eintrag, kein Feld: Verweis **oder** freie Anschrift
 * **oder** „vom Projekt" — gespeichert wird der Eintrag als Ganzes, wenn ihn
 * der Fokus verlässt (8.3 a).
 */
function BaustelleFeld({
  stelle,
  geerbt,
  freiGeerbt,
  gesperrt,
  partner,
  titelProjekt,
}: {
  stelle: Speicherstelle<unknown>
  geerbt: Geerbt
  freiGeerbt: Geerbt
  gesperrt: boolean
  partner: PartnerAuswahl | null
  titelProjekt: string
}) {
  const id = geerbt?.own as string | null | undefined
  const frei = freiGeerbt?.own as Record<string, string> | null | undefined
  const eintrag = useEntwurf<BaustelleEntwurf>(stelle, {
    kennung: 'siteAddress',
    gespeichert: { wahl: id ? id : frei ? 'own' : 'inherit', frei: { street: '', zip: '', city: '', country: 'AT', ...(frei ?? {}) } },
    felder: ['siteAddressId', 'siteAddressJson'],
    label: 'Baustelle',
    gesperrt,
    operation: (e) => () => {
      if (e.wahl === 'inherit') return { siteAddressId: null, siteAddressJson: null }
      if (e.wahl === 'own')
        return {
          siteAddressId: null,
          siteAddressJson: Object.fromEntries(Object.entries(e.frei).filter(([, v]) => String(v ?? '').trim() !== '')),
        }
      return { siteAddressId: e.wahl, siteAddressJson: null }
    },
  })
  const wert = eintrag.entwurf ?? { wahl: 'inherit', frei: {} }
  const setze = (teil: Partial<BaustelleEntwurf>) => eintrag.setze((alt) => ({ ...alt, ...teil }))
  const wirksam = geerbt?.effective ? geerbt : freiGeerbt

  /**
   * Eine Auswahl speichert sofort (3.2), die freie Anschrift erst beim
   * Verlassen. Gesendet wird im Effekt, nicht im Rückruf: `speichere` liest den
   * Entwurf, den der letzte Render gesehen hat — unmittelbar nach `setze` wäre
   * das noch der alte.
   */
  const sofort = useRef(false)
  useEffect(() => {
    if (!sofort.current) return
    sofort.current = false
    void eintrag.speichere()
  })

  return (
    <div className="space-y-1" {...eintrag.huelle}>
      <FeldZeile label="Baustelle" status={eintrag} hilfe={herkunft(wirksam, titelProjekt)}>
        <select
          className={feldKlasse}
          disabled={gesperrt || undefined}
          value={wert.wahl}
          onChange={(e) => {
            setze({ wahl: e.target.value })
            if (e.target.value !== 'own') sofort.current = true
          }}
        >
          <option value="inherit">vom {titelProjekt} übernehmen</option>
          {wert.wahl !== 'inherit' && wert.wahl !== 'own' && !partner?.addresses.some((a) => a.id === wert.wahl) && (
            <option value={wert.wahl}>{text(geerbt?.effective)}</option>
          )}
          {(partner?.addresses ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.label || a.type}: {a.street}, {a.zip} {a.city}
            </option>
          ))}
          <option value="own">eigene Anschrift …</option>
        </select>
      </FeldZeile>
      {wert.wahl === 'own' && (
        <div className="grid gap-1 sm:ml-[10.75rem] sm:grid-cols-6">
          <input
            className={`${feldKlasse} sm:col-span-6`}
            placeholder="Straße"
            disabled={gesperrt || undefined}
            value={wert.frei.street ?? ''}
            onChange={(e) => setze({ frei: { ...wert.frei, street: e.target.value } })}
          />
          <input
            className={`${feldKlasse} sm:col-span-1`}
            placeholder="PLZ"
            disabled={gesperrt || undefined}
            value={wert.frei.zip ?? ''}
            onChange={(e) => setze({ frei: { ...wert.frei, zip: e.target.value } })}
          />
          <input
            className={`${feldKlasse} sm:col-span-4`}
            placeholder="Ort"
            disabled={gesperrt || undefined}
            value={wert.frei.city ?? ''}
            onChange={(e) => setze({ frei: { ...wert.frei, city: e.target.value } })}
          />
          <input
            className={`${feldKlasse} sm:col-span-1`}
            placeholder="Land"
            maxLength={2}
            disabled={gesperrt || undefined}
            value={wert.frei.country ?? ''}
            onChange={(e) => setze({ frei: { ...wert.frei, country: e.target.value.toUpperCase() } })}
          />
        </div>
      )}
    </div>
  )
}

/** Schema und Intervall der Position — leer heißt „vom Projekt". */
function AbrechnungsFelder({
  stelle,
  gesperrt,
  geerbtesSchema,
  titelProjekt,
}: {
  stelle: Speicherstelle<unknown>
  gesperrt: boolean
  geerbtesSchema: string
  titelProjekt: string
}) {
  const intervallJetzt = useRef<string | null>(null)
  const schema = useFeld<string | null>(stelle, 'billingScheme', {
    art: 'auswahl',
    label: 'Abrechnungsschema',
    gesperrt,
    // Passt das Intervall nicht zum wirksamen Schema, geht das erste erlaubte als Folgefeld mit.
    folge: (w) => {
      const wirksam = w || geerbtesSchema
      if (!intervallJetzt.current || billingIntervalAllowed(wirksam, intervallJetzt.current)) return {}
      return { billingInterval: billingIntervalsFor(wirksam)[0] ?? null }
    },
  })
  const intervall = useFeld<string | null>(stelle, 'billingInterval', { art: 'auswahl', label: 'Abrechnungsintervall', gesperrt })
  intervallJetzt.current = intervall.wert
  const erlaubt = billingIntervalsFor(schema.wert || geerbtesSchema)

  return (
    <FeldZeile label="Abrechnung" status={schema}>
      <span className="flex flex-wrap gap-2">
        <select className={`${feldKlasse} w-auto`} {...schema.props}>
          <option value="">vom {titelProjekt}</option>
          {PROJECT_BILLING_SCHEMES.map((s) => (
            <option key={s} value={s}>
              {BILLING_SCHEME_LABELS[s] ?? s}
            </option>
          ))}
        </select>
        <select className={`${feldKlasse} w-auto`} {...intervall.props}>
          <option value="">vom {titelProjekt}</option>
          {erlaubt.map((s) => (
            <option key={s} value={s}>
              {BILLING_INTERVAL_LABELS[s] ?? s}
            </option>
          ))}
        </select>
        <FeldStatus status={intervall} zeichen={false} />
      </span>
    </FeldZeile>
  )
}
